import React, { useMemo, useRef } from 'react';
import FirebaseAuthManager from '../utils/firebaseAuthManager';
import FirebaseEventManager from '../utils/firebaseEventManager';
import AmplitudeEventManager from '../utils/amplitudeEventManager';
import VerifyAccount from './components/VerifyAccount';
import EmailSignIn from './components/EmailSignIn';
import DefaultSignIn from './components/DefaultSignIn';
import handleLogin, { createJIDSuffix, generateLocalStorageKey, removeAuth } from '../utils/handleLogin';
import ApiManager, { AuthData, ErrorType } from '../utils/ApiManager';

let timeoutIndex: NodeJS.Timeout | undefined = undefined;
const isFirstSignInCacheKey = 'ALFRED:SIGNED';
const firebaseAuthManager = new FirebaseAuthManager();
const firebaseEventManager = new FirebaseEventManager();

export default function SignIn(props: {
  isLoginFinish: boolean;
  backToSignIn?: boolean;
  showAnonymous?: boolean;
  backgroundImage: string;
  role: string;
  productName: string;
  productVersion: string;
  hideLogoImg?: boolean;
  hideSignInIntro?: boolean;
  localStorageKey?: string;
  jidSuffix?: string;
  onSignInSuccess: (data: {
    idToken: string;
    accountMail: string | null;
    displayName: string | null;
    photoURL: string;
  } & Omit<AuthData, 'email' | 'expireAt'>) => void;
  retryPasswordTooManyTimes: () => void;
  networkError: () => void;
  apiError: () => void;
  unVerifiedError: () => void;
  onNotSignIn: () => void;
  onEmailSignIn: (isEmailLogin: boolean) => void;
  setIsLoading?: (isLoading: boolean) => void;
  downloadApp?: (data: { platform: string; page: string }) => void;
  children?: React.ReactNode;
}) {
  const [isEmailLogin, setIsEmailLogin] = React.useState(!props.backToSignIn);
  const [emailCache, setEmailCache] = React.useState<string>('');
  const [passwordCache, setPasswordCache] = React.useState<string>('');
  const [user, setUser] = React.useState(firebaseAuthManager.getCurrentUser());
  const localStorageKey = useMemo(() => props.localStorageKey || generateLocalStorageKey(props.productName), [props.localStorageKey, props.productName]);
  const loginType = useRef('manual');
  const provider = useRef('');  
  const startTime = useRef(new Date().getTime());

  React.useEffect(() => {
    firebaseEventManager.signInPageLanded(props.productVersion);

    let isFirstSignInCache = localStorage.getItem(isFirstSignInCacheKey);
    if (!isFirstSignInCache) {
      loginType.current = 'first';
    }
    async function main() {
      try {
        const res = await handleLogin({
          localStorageKey,
          jidSuffix: props.jidSuffix,
          deviceName: props.productName,
          deviceType: props.role,
          handleSignOut,
        });
        if (res) {
          loginType.current = res.loginType;
          provider.current = res.provider;
          startTime.current = res.startTime;
          if (res.alfredIsLogin) {
            await onSignInSuccess(res.authData);
          }
        } else {
          props.onNotSignIn();
        }
      } catch (error) {
        console.log(error);
        if (
          error instanceof Error &&
          error.message === ErrorType.ForceSignOut
        ) {
          await handleSignOut();
        }
      }
    }
    main();

    return () => {};
  }, []);

  React.useEffect(() => {
    props.onEmailSignIn(isEmailLogin);
  }, [isEmailLogin]);

  React.useEffect(() => {
    setIsEmailLogin(!props.backToSignIn);
  }, [props.backToSignIn]);

  async function handleSignOut() {
    await firebaseAuthManager.signOut();
    AmplitudeEventManager.resetUser();
    firebaseEventManager.clearUserProperties();
    removeAuth(localStorageKey);
    props.onNotSignIn();
  }

  async function signInWithAnonymous() {
    provider.current = 'anonymous';
    try {
      await firebaseAuthManager.signInWithAnonymous();
      AmplitudeEventManager.setSignInChannel(provider.current);
      await onSignInSuccess();
    } catch (error) {
      firebaseEventManager.sendLoginFailed({
        provider: provider.current,
        type: loginType.current,
        code: (error as { code?: string }).code || null,
      });
      AmplitudeEventManager.signInSuccess({ success: false, method: provider.current });
    }
  }

  async function signInWithGoogle() {
    if (isEmailLogin) {
      firebaseEventManager.signInEmailPageClick({
        item: 'google',
      });
    } else {
      firebaseEventManager.signInPageClickItem({
        item: 'google_login',
      });
    }
    startTime.current = new Date().getTime();
    provider.current = 'google';
    try {
      if (props.showAnonymous && user?.isAnonymous) {
        await firebaseAuthManager.linkWithGoogle(user);
      } else {
        await firebaseAuthManager.signInWithGoogle();
      }
      AmplitudeEventManager.setSignInChannel(provider.current);
      await onSignInSuccess();
    } catch (error) {
      firebaseEventManager.sendLoginFailed({
        provider: provider.current,
        type: loginType.current,
        code: (error as { code?: string }).code || null,
      });
      AmplitudeEventManager.signInSuccess({ success: false, method: provider.current });
    }
  }

  async function signInWithApple() {
    if (isEmailLogin) {
      firebaseEventManager.signInEmailPageClick({
        item: 'apple',
      });
    } else {
      firebaseEventManager.signInPageClickItem({
        item: 'apple_login',
      });
    }
    startTime.current = new Date().getTime();
    provider.current = 'apple';
    try {
      if (props.showAnonymous && user?.isAnonymous) {
        await firebaseAuthManager.linkWithApple(user);
      } else {
        await firebaseAuthManager.signInWithApple();
      }
      AmplitudeEventManager.setSignInChannel(provider.current);
      await onSignInSuccess();
    } catch (error) {
      firebaseEventManager.sendLoginFailed({
        provider: provider.current,
        type: loginType.current,
        code: (error as { code?: string }).code || null,
      });
      AmplitudeEventManager.signInSuccess({ success: false, method: provider.current });
    }
  }

  async function signInWithEmail({
    email,
    password,
    error = null,
  }: {
    email?: string;
    password?: string;
    error?: Error | null;
  }) {
    if (email) setEmailCache(email);
    if (password) setPasswordCache(password);
    let errorString: string | null = null;
    let errorCode: string | null = null;
    startTime.current = new Date().getTime();
    provider.current = 'email';
    try {
      if (error !== null) {
        throw error;
      }

      await Promise.race([
        timeout(),
        (async function () {
          if (email && password) {
            await firebaseAuthManager.signInWithEmail({
              email,
              password,
            });
          }
          await onSignInSuccess();
        })(),
      ]);
      AmplitudeEventManager.setSignInChannel(provider.current);

      return true;
    } catch (error) {
      const errorMessage = (error as { message?: string }).message;
      errorCode = (error as { code?: string }).code || null;
      AmplitudeEventManager.signInSuccess({ success: false, method: provider.current });
      switch (errorMessage) {
        case 'Timeout':
          props.apiError();
          break;
        case 'ExceedTryMax':
          errorString = 'pwd_retry_limit';
          props.retryPasswordTooManyTimes();
          break;
        default:
          break;
      }
      switch (errorCode) {
        case 'auth/network-request-failed':
          props.networkError();
          break;
        case 'auth/too-many-requests':
          props.retryPasswordTooManyTimes();
          break;
        default:
          break;
      }
      throw error;
    } finally {
      clearTimeout(timeoutIndex);
      if (errorString !== null) {
        firebaseEventManager.sendLoginFailed({
          provider: provider.current,
          type: loginType.current,
          code: errorCode,
          customError: errorString,
        });
      }
    }
  }

  async function onSignInSuccess(
    authData?: Omit<AuthData, 'email' | 'expireAt'>
  ) {
    if (props.setIsLoading) {
      props.setIsLoading(true);
    }
    localStorage.setItem(isFirstSignInCacheKey, '1');
    const user = firebaseAuthManager.getCurrentUser();
    setUser(user);

    if (user && !user.isAnonymous && !user.emailVerified) {
      firebaseEventManager.sendLoginFailed({
        provider: 'email',
        type: loginType.current,
        code: '',
        customError: 'unverified',
      });
      AmplitudeEventManager.signInSuccess({
        success: false,
        method: 'email',
      });
      if (props.setIsLoading) {
        props.setIsLoading(false);
      }
    }

    if (!user || (!user.isAnonymous && !user.emailVerified) || !user.refreshToken) {
      await handleSignOut();
      if (props.setIsLoading) {
        props.setIsLoading(false);
      }
      return setIsEmailLogin(true);
    }

    try {
      const idToken = await user.getIdToken();
      let shouldUpdate = false;
      let displayName = user.displayName;
      if (!displayName) {
        displayName = user.providerData.find(
          (profile) => profile.displayName
        )?.displayName || '';
        shouldUpdate = !!displayName;
      }
      let photoURL = user.photoURL;
      if (provider.current === 'email' || !photoURL) {
        photoURL = 'https://cdn.my-alfred.com/emailUserAvatar.png';
        shouldUpdate = true;
      }

      if (shouldUpdate) {
        await firebaseAuthManager.updateProfile(user, {
          displayName,
          photoURL,
        });
      }

      if (!authData) {
        const resource = props.jidSuffix || createJIDSuffix(props.productName, props.role);
        authData = await ApiManager.getKvTokenAndResetData({
          firebaseUid: user.uid,
          idToken,
          resource,
          localStorageKey,
          startTime: startTime.current,
        });
        AmplitudeEventManager.setDefaultEventProperties({
          jid: authData.jid,
        });
      }
      props.onSignInSuccess({
        idToken,
        accountMail: user.email,
        displayName,
        photoURL,
        ...authData,
      });

      firebaseEventManager.sendInitialLoginSucceeded({
        provider: provider.current,
        email: user.email || '',
        type: loginType.current,
      });

      firebaseEventManager.logingTime({
        provider: provider.current,
        btnclickLogin: new Date().getTime() - startTime.current,
      });
    } catch (error) {
      await handleSignOut();
      if (props.setIsLoading) {
        props.setIsLoading(false);
      }
    }
  }

  async function goSignInWithEmailPage() {
    if (user && user.email) {
      await firebaseAuthManager.signOut();
    }
    firebaseEventManager.signInPageClickItem({
      item: 'email_login',
    });
    setIsEmailLogin(true);
  }

  async function verifyAccountAgain() {
    firebaseEventManager.verifyPageClick();
    const result = await signInWithEmail({
      email: emailCache,
      password: passwordCache,
    });
    if (result !== true) {
      return;
    }

    const user = firebaseAuthManager.getCurrentUser();
    if (user?.emailVerified === false) {
      props.unVerifiedError();
    }
  }

  function downloadApp({ platform, page }: { platform: string; page: string }) {
    let iOSLink = 'https://alfredcamera.app/store/webviewer_emailsignup_ios';
    let androidLink =
      'https://alfredcamera.app/store/webviewer_emailsignup_android';
    if (props.role === 'camera') {
      iOSLink = 'https://alfredcamera.app/store/webcamera_emailsignup_ios';
      androidLink =
        'https://alfredcamera.app/store/webcamera_emailsignup_android';
    }
    window.open(platform === 'ios' ? iOSLink : androidLink);
  }

  // show nothing
  if (props.isLoginFinish === false) {
    return null;
  }

  if (user && !user.isAnonymous && !user.emailVerified) {
    if (emailCache === '') {
      firebaseAuthManager.signOut();
      setUser(null);
      return null;
    }

    return (
      <VerifyAccount
        downloadApp={downloadApp}
        email={user.email || ''}
        verifyAccountAgain={verifyAccountAgain}
      />
    );
  }

  if (isEmailLogin) {
    return (
      <EmailSignIn
        downloadApp={props.downloadApp ? props.downloadApp : downloadApp}
        signInWithGoogle={signInWithGoogle}
        signInWithEmail={signInWithEmail}
        signInWithApple={signInWithApple}
        fetchSignInMethodsForEmail={(email) =>
          firebaseAuthManager.fetchSignInMethodsForEmail(email)
        }
        hideLogoImg={props.hideLogoImg}
        hideSignInIntro={props.hideSignInIntro}
      />
    );
  }

  if (props.children) {
    return (
      <>
        {React.Children.map(props.children, (child) => {
          // checking isValidElement is the safe way and avoids a typescript error too
          const props = {
            signInWithGoogle,
            signInWithApple,
            goSignInWithEmailPage,
          };
          // When child is DOM element then typeof child.type === 'string'
          // For preventing react throws warning, then filter DOM element
          if (React.isValidElement(child) && typeof child.type !== 'string') {
            return React.cloneElement(child, props);
          }
          return child;
        })}
      </>
    );
  } else {
    return (
      <DefaultSignIn
        showAnonymous={props.showAnonymous}
        backgroundImage={props.backgroundImage}
        signInWithGoogle={signInWithGoogle}
        signInWithApple={signInWithApple}
        signInWithAnonymous={signInWithAnonymous}
        goSignInWithEmailPage={goSignInWithEmailPage}
      />
    );
  }
}

async function timeout() {
  await new Promise((res, rej) => {
    timeoutIndex = setTimeout(() => {
      rej(new Error('Timeout'));
    }, 60000);
  });
}
